import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { setupApiDebugger } from '../utils/apiDebugger';
import {
  clearAuthSession,
  getRefreshToken,
  storeAuthSession,
} from '../auth/token-storage';

const api = axios.create({
  baseURL: import.meta.env.VITE_BASE_TICKETIN_BACKEND_URL,
  // baseURL: import.meta.env.VITE_BASE_TICKETIN_BACKEND_DEV,
});

// Setup API debugger in development mode
if (import.meta.env.DEV) {
  setupApiDebugger(api);
}

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshInFlight: Promise<string> | null = null;

const isTokenEndpoint = (url?: string) =>
  url === '/auth/refresh' || url === '/auth/logout';

const doesNotNeedBearer = (url?: string) =>
  isTokenEndpoint(url) || url === '/auth';

const handleUnauthorizedError = () => {
  clearAuthSession();
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

/**
 * One shared refresh operation prevents concurrent 401 responses from using a
 * rotated refresh token twice. It is also used by the WhatsApp socket when it
 * reconnects after an access token expires.
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token is available');

      const { data } = await api.post('/auth/refresh', { refreshToken });
      if (
        !data ||
        typeof data.token !== 'string' ||
        !data.token ||
        typeof data.refreshToken !== 'string' ||
        !data.refreshToken
      ) {
        throw new Error('Ticketin returned an invalid token refresh response');
      }

      storeAuthSession(data);
      return data.token;
    })().finally(() => {
      refreshInFlight = null;
    });
  }

  try {
    return await refreshInFlight;
  } catch (error) {
    handleUnauthorizedError();
    throw error;
  }
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as RetriableRequestConfig | undefined;
    const status = error.response?.status;

    if (status !== 401 || !original || isTokenEndpoint(original.url)) {
      return Promise.reject(error);
    }
    if (original._retry) {
      handleUnauthorizedError();
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      const token = await refreshAccessToken();
      original.headers.Authorization = `Bearer ${token}`;
      return api(original);
    } catch {
      return Promise.reject(error);
    }
  }
);

api.interceptors.request.use(
  (config) => {
    const accessToken: string | null = localStorage.getItem('token');

    if (accessToken && config.headers && !doesNotNeedBearer(config.url)) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export { api };
